// /verify/<number>/<token>: the public check behind a certificate's link (Certificate.dc.html, "What
// the check link shows": the name, the course, the cohort, the date finished and the certificate
// number, issued by Bull and Bear Investments Limited; nothing else). It opens logged out and logged
// in alike, before the login door, so App routes basePath '/verify' here ahead of the auth check.
// No board draws this page, so it uses the shell's master brand look and its words are in the
// stage 4 batch for Adam.
import { useCallback, useEffect, useState } from 'react'
import { setTitle } from '../config/site.js'
import { usePhone } from '../lib/media.js'
import { NOT_FOUND_LINE, certificateDate, checkCertificate, cohortLine, parseVerifyPath } from '../lib/certificateCheck.js'
import { Wordmark } from '../shell/Marks.jsx'
import { Footer } from '../shell/Shell.jsx'
import '../styles/verify.css'

export default function Verify({ path }) {
  const phone = usePhone()
  const target = parseVerifyPath(path)
  const number = target?.number ?? null
  const token = target?.token ?? null
  const [result, setResult] = useState(target ? { status: 'checking' } : { status: 'not_found', message: NOT_FOUND_LINE })

  useEffect(() => { setTitle('Certificate check') }, [])

  const run = useCallback(async () => {
    if (!number || !token) return
    setResult({ status: 'checking' })
    setResult(await checkCertificate(number, token))
  }, [number, token])

  useEffect(() => { run() }, [run])

  return (
    <div className="vf-page">
      <header className="vf-header">
        <Wordmark className={phone ? 'mark mark--phone' : 'mark mark--header'} />
      </header>
      <main className="vf-main" aria-live="polite">
        <div className="vf-eyebrow">Certificate check</div>
        <Body result={result} onRetry={run} />
      </main>
      <Footer phone={phone} />
    </div>
  )
}

function Body({ result, onRetry }) {
  if (result.status === 'checking') return <h1 className="vf-h1">Checking the certificate</h1>
  if (result.status === 'not_found') {
    return (
      <>
        <h1 className="vf-h1">No certificate found</h1>
        <p className="vf-lede">{result.message}</p>
      </>
    )
  }
  if (result.status === 'limited') {
    return (
      <>
        <h1 className="vf-h1">Too many checks in a row</h1>
        <p className="vf-lede">{result.message}</p>
      </>
    )
  }
  if (result.status === 'error') {
    return (
      <>
        <h1 className="vf-h1">That didn't load</h1>
        <p className="vf-lede">{result.message}</p>
        <p className="vf-actions"><button type="button" className="btn btn-ink" onClick={onRetry}>Try again</button></p>
      </>
    )
  }
  const c = result.certificate
  const cohort = cohortLine(c)
  return (
    <>
      <h1 className="vf-h1">{c.name}</h1>
      <p className="vf-lede">has completed {c.course}.</p>
      <dl className="vf-facts">
        <div><dt>Course</dt><dd>{c.course}</dd></div>
        {cohort && <div><dt>Cohort</dt><dd>{cohort}</dd></div>}
        <div><dt>Finished</dt><dd>{certificateDate(c.completed_on)}</dd></div>
        <div><dt>Certificate</dt><dd>{c.number}</dd></div>
        <div><dt>Issued by</dt><dd>{c.issuer}</dd></div>
      </dl>
      <p className="vf-note">This is the certificate's own record in Adam Slee's members hub. It shows only what's on the certificate.</p>
    </>
  )
}
